import {
  type ProductListingBulkIdentitiesRequest,
  productListingBulkIdentitiesResponseSchema,
  productListingGroupResponseSchema,
  productListingListResponseSchema,
  productListingRefreshResponseSchema,
  resolveProductListingsResponseSchema,
} from "@adpilot/shared";
import { z } from "zod";
import { api } from "@/lib/api";

export interface ListingFilters {
  search?: string;
  active?: "true" | "false" | "all";
  availability?: string;
  productStatus?: "advertised" | "unadvertised";
  adProduct?: "sp";
}

export interface ProductListingGroupSort {
  sortBy: "type" | "price" | "inventory";
  sortOrder: "asc" | "desc";
}

function searchParams(filters: ListingFilters, page: number, variationGroupKey?: string) {
  return {
    page,
    pageSize: 20,
    active: filters.active ?? "true",
    ...(filters.adProduct ? { adProduct: filters.adProduct } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.availability ? { availability: filters.availability } : {}),
    ...(filters.productStatus ? { productStatus: filters.productStatus } : {}),
    ...(variationGroupKey ? { variationGroupKey } : {}),
  };
}

export async function fetchProductListingGroups(
  shopId: string,
  filters: ListingFilters,
  page: number,
  sort?: ProductListingGroupSort,
) {
  const response = await api
    .get("product-listings/groups", {
      headers: { "X-Shop-Id": shopId },
      searchParams: {
        ...searchParams(filters, page),
        ...(sort ? sort : {}),
      },
    })
    .json<unknown>();
  return productListingGroupResponseSchema.parse(response);
}

export async function fetchProductListings(
  shopId: string,
  variationGroupKey: string | undefined,
  filters: ListingFilters,
  page: number,
) {
  const response = await api
    .get("product-listings", {
      headers: { "X-Shop-Id": shopId },
      searchParams: searchParams(filters, page, variationGroupKey),
      ...(filters.adProduct ? { timeout: 120_000, retry: 0 } : {}),
    })
    .json<unknown>();
  return productListingListResponseSchema.parse(response);
}

export async function resolveProductListings(
  shopId: string,
  identifiers: string[],
  adProduct?: "sp",
) {
  const response = await api
    .post("product-listings/resolve", {
      headers: { "X-Shop-Id": shopId },
      json: { identifiers, ...(adProduct ? { adProduct } : {}) },
      ...(adProduct ? { timeout: 120_000 } : {}),
    })
    .json<unknown>();
  return z
    .object({ success: z.literal(true), data: resolveProductListingsResponseSchema })
    .parse(response).data;
}

export async function fetchBulkProductListingIdentities(
  shopId: string,
  payload: ProductListingBulkIdentitiesRequest,
) {
  const response = await api
    .post("product-listings/bulk-identities", {
      headers: { "X-Shop-Id": shopId },
      json: payload,
      ...(payload.filters.adProduct ? { timeout: 120_000 } : {}),
    })
    .json<unknown>();
  return z
    .object({ success: z.literal(true), data: productListingBulkIdentitiesResponseSchema })
    .parse(response).data;
}

export async function refreshProductListings(shopId: string) {
  const response = await api
    .post("product-listings/refresh", { headers: { "X-Shop-Id": shopId } })
    .json<unknown>();
  return z
    .object({ success: z.literal(true), data: productListingRefreshResponseSchema })
    .parse(response).data;
}

export async function refreshProductInventory(shopId: string) {
  const response = await api
    .post("product-listings/inventory/refresh", {
      headers: { "X-Shop-Id": shopId },
      timeout: 120_000,
      retry: 0,
    })
    .json<unknown>();
  return z
    .object({
      success: z.literal(true),
      data: z
        .object({ status: z.enum(["success", "partial"]), updated: z.number().int().nonnegative() })
        .strict(),
    })
    .strict()
    .parse(response).data;
}
